import { useAtomValue } from 'jotai';
import { useParams } from 'react-router-dom';

import { useTranslate } from '@growtherapy/i18n';
import { Button, Subhead } from '@growtherapy/sprout-ui';

import ChairAndArmchair from '../../assets/chair-and-armchair.svg';
import { Navbar, PageContainer, PageProviders } from '../../components';
import { useGetTelehealthSessionInfo } from '../../hooks';
import { Theme } from '../../state';
import { useEnterWaitingRoomCallback } from '../nav/useEnterWaitingRoomCallback';
import { VisitorState, visitorNameAtom, visitorStateAtom } from '../state';

export type RejoinPageProps = {
  providerName: string;
  isTimeout: boolean;
  onRejoin: () => void;
};

export function Page({
  providerName,
  isTimeout,
  onRejoin,
}: {
  onRejoin: () => void;
  providerName: string;
  isTimeout: boolean;
}) {
  const t = useTranslate();
  const timeOutMessage = isTimeout
    ? t('telehealth-core:rejoin.looksLikeYouveBeenWaiting', { providerName })
    : t('telehealth-core:rejoin.youNavigatedAwayFromTheWaitingRoom', {
        providerName,
      });

  return (
    <PageProviders theme={Theme.LIGHT}>
      <PageContainer>
        <Navbar showLanguageSwitcher />
        <div className='border-t bg-neutral-200 h-full'>
          <div className='px-6 flex flex-col items-center justify-center gap-4'>
            <img src={ChairAndArmchair} alt='' className='max-w-full pt-10 rebrand:hidden' />
            <Subhead.Default
              as='h1'
              className='font-medium text-medium text-center max-w-[40rem] pt-4 rebrand:font-normal rebrand:text-large rebrand:pt-16 rebrand:pb-4'
            >
              {timeOutMessage}
            </Subhead.Default>
            <Button className='w-full sm:w-auto' onClick={onRejoin}>
              {t('telehealth-core:rejoin.rejoinWaitingRoom')}
            </Button>
          </div>
        </div>
      </PageContainer>
    </PageProviders>
  );
}

export function PageWrapper() {
  const t = useTranslate();
  const { providerShortId, patientShortId } = useParams();
  const { data } = useGetTelehealthSessionInfo(providerShortId, patientShortId);
  const visitorState = useAtomValue(visitorStateAtom);
  const enterWaitingRoom = useEnterWaitingRoomCallback();
  const visitorName = useAtomValue(visitorNameAtom);

  return (
    <Page
      providerName={data?.telehealthSessionInfo?.provider.name ?? t('telehealth-core:rejoin.yourProvider')}
      isTimeout={visitorState === VisitorState.TIMEOUT}
      onRejoin={() => {
        enterWaitingRoom(visitorName);
      }}
    />
  );
}
